import i18n from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpBackend from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import api from "./lib/axiosConfig";

const API_URL = import.meta.env.VITE_BACK_URL;

export const supportedLocales = ["fr", "en", "es", "nl"] as const;

export const multilingualFieldSchema = <T extends z.ZodType>(type: T) =>
  z.record(z.enum(supportedLocales), type.optional());

// equivalent to multilingualFieldSchema
export type LocaleDict<T = string> = {
  [locale in SupportedLocale]?: T;
};

export const constructLocaleDict = <T>(
  func: (lang: SupportedLocale) => T | undefined,
): LocaleDict<T> =>
  supportedLocales.reduce((acc, lang) => ({ ...acc, [lang]: func(lang) }), {});

let mainLanguage: SupportedLocale;

export const getMainLanguage = () =>
  mainLanguage ?? localStorage.getItem("main-language") ?? "en";

export const setMainLanguage = (lang: SupportedLocale) => {
  mainLanguage = lang;
  localStorage.setItem("main-language", lang);
  i18n.changeLanguage(lang);
};

export function fetchMainLanguage() {
  api.get("back/api/instance/current").then((instanceData) => {
    setMainLanguage(instanceData.data.mainLanguage);
  });
}

export type SupportedLocale = (typeof supportedLocales)[number];

export const getDecimalSeparator = (locale: SupportedLocale) => {
  const numberWithDecimalSeparator = 1.1;
  const part = Intl.NumberFormat(locale)
    .formatToParts(numberWithDecimalSeparator)
    .find((part) => part.type === "decimal");
  return part?.value || ".";
};

export const getThousandSeparator = (locale: SupportedLocale) => {
  const numberWithThousandSeparator = 100000;
  const part = Intl.NumberFormat(locale)
    .formatToParts(numberWithThousandSeparator)
    .find((part) => part.type === "group");
  return part?.value || ",";
};

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  (supportedLocales as unknown as string[]).includes(locale);

i18n
  .use(ChainedBackend)
  .use(initReactI18next)
  .init({
    lng: getMainLanguage(),
    supportedLngs: supportedLocales,
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
    },
    backend: {
      backends: [LocalStorageBackend, HttpBackend],
      backendOptions: [
        {
          expirationTime:
            1000 *
              parseInt(
                import.meta.env.VITE_TRANSLATIONS_REFRESH_TIME_SECONDS,
              ) || 10 * 1000, // 10 seconds
        },
        {
          loadPath: `${API_URL}back/api/translations/{{lng}}/app/app-formatted`,
        },
      ],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on("languageChanged", (lng) => {
  localStorage.setItem("language", lng);
});

export default i18n;
